import { useMemo } from "react";
import { useTrackContext } from "@/state/TrackContext";
import { TSelectedVariants } from "@/types";
import { getPostcodeDiscountAdjustmentFromCartLine } from "@/shared/utils/postcodeDiscount";
import { hasLargeGroupSurchargeOnCartLine } from "@/shared/utils/sessionPrice";

export const useSelectedLineItems = (cartData: any[]) => {
  const { productRenderedLocation, mainTrackSelection, battleKartSelection, miniTrackSection, juniorTrackSelection, intermediateTrackSelection, shopifyCartVariants } =
    useTrackContext();

  const selectedLineItems: TSelectedVariants[] = useMemo(() => {
    // Combine Redux state variants and shopifyCartVariants like release/dev does
    const reduxVariants: TSelectedVariants[] = [
      ...mainTrackSelection.variantsAndDate.selectedVariants,
      ...battleKartSelection.variantsAndDate.selectedVariants,
      ...miniTrackSection.variantsAndDate.selectedVariants,
      ...intermediateTrackSelection.variantsAndDate.selectedVariants,
      ...juniorTrackSelection.variantsAndDate.selectedVariants,
    ];

    // Convert cart items to TSelectedVariants format
    const cartVariants: TSelectedVariants[] = cartData.map((item: any) => {
      if (productRenderedLocation === "external") {
        // Headless cart item structure
        return {
          variantSelectedTime: new Date(),
          variantsSelectedObject: {
            id: item.merchandise.id,
            title: item.merchandise.title,
            product: {
              id: item.merchandise.product.id,
              title: item.merchandise.product.title,
              productType: item.merchandise.product.productType,
            },
            priceV2: item.merchandise.priceV2,
            quantityAvailable: item.merchandise.quantityAvailable,
            availableForSale: item.merchandise.availableForSale,
            unitPrice: item.merchandise.priceV2,
            requiresShipping: item.merchandise.requiresShipping,
            currentlyNotInStock: item.merchandise.currentlyNotInStock,
            barcode: item.merchandise.barcode,
            image: item.merchandise.image,
            price: item.merchandise.priceV2.amount,
            selectedOptions: item.merchandise.selectedOptions || [],
            sku: item.merchandise.sku,
            postcodeDiscountAdjustment: getPostcodeDiscountAdjustmentFromCartLine(item),
            largeGroupSurcharge: hasLargeGroupSurchargeOnCartLine(item),
          },
        };
      } else {
        // Shopify cart item structure
        return {
          variantSelectedTime: new Date(),
          variantsSelectedObject: {
            id: `gid://shopify/ProductVariant/${item.variant_id}`,
            title: item.variant_title,
            product: {
              id: `gid://shopify/Product/${item.product_id}`,
              title: item.product_title,
              productType: item.product_type,
            },
            priceV2: {
              amount: (item.price / 100).toString(),
              currencyCode: "AUD",
            },
            quantityAvailable: 999,
            availableForSale: true,
            unitPrice: {
              amount: (item.price / 100).toString(),
              currencyCode: "AUD",
            },
            requiresShipping: item.requires_shipping,
            currentlyNotInStock: false,
            barcode: null,
            image: {
              altText: null,
              url: item.image || "",
              height: 0,
              src: item.image || "",
              width: 0,
              originalSrc: item.image || "",
              id: "",
            },
            price: (item.price / 100).toString(),
            selectedOptions: item.options_with_values || [],
            sku: item.sku,
            postcodeDiscountAdjustment: getPostcodeDiscountAdjustmentFromCartLine(item),
            largeGroupSurcharge: hasLargeGroupSurchargeOnCartLine(item),
          },
        };
      }
    });

    // Build a set of all variant IDs that Redux considers "selected".
    // This is the source of truth — shopifyCartVariants and cartData can be stale
    // after removal, so we must not let them re-introduce removed items.
    const reduxVariantIds = new Set(reduxVariants.map((v) => v.variantsSelectedObject.id));

    // For Shopify theme, combine Redux variants with shopifyCartVariants and cart items
    if (productRenderedLocation !== "external") {
      // Only include shopifyCartVariants / cartVariants that are NOT already
      // covered by Redux state. This prevents stale cart data from overriding
      // the authoritative Redux selectedVariants after a removal.
      const supplementalVariants = [...(shopifyCartVariants || []), ...cartVariants].filter(
        (v) => !reduxVariantIds.has(v.variantsSelectedObject.id),
      );

      const allVariants = [...reduxVariants, ...supplementalVariants];

      // Remove duplicates by ID (Redux state takes precedence)
      return allVariants.filter(
        (variant, index, array) => index === array.findIndex((v) => v.variantsSelectedObject.id === variant.variantsSelectedObject.id),
      );
    }

    // For external (headless), combine Redux state with cart items
    const supplementalVariants = cartVariants.filter((v) => !reduxVariantIds.has(v.variantsSelectedObject.id));
    const allVariants = [...reduxVariants, ...supplementalVariants];

    return allVariants.filter(
      (variant, index, array) => index === array.findIndex((v) => v.variantsSelectedObject.id === variant.variantsSelectedObject.id),
    );
  }, [
    productRenderedLocation,
    shopifyCartVariants,
    mainTrackSelection,
    battleKartSelection,
    miniTrackSection,
    intermediateTrackSelection,
    juniorTrackSelection,
    cartData,
  ]);

  return selectedLineItems;
};
